import type { Company, ThemeClasses } from "../types";
import { CompanyActionsMenu } from "./CompanyActionsMenu";

interface CompanyListRowProps {
  company: Company;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onDetails: (company: Company) => void;
  onEdit: (company: Company) => void;
  onDelete?: (company: Company) => void;
  onActivate?: (company: Company) => void;
  onSoftDelete?: (company: Company) => void;
  onSources?: (company: Company) => void;
  onRetransmissions?: (company: Company) => void;
  isSelectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelection?: (company: Company) => void;
}

export function CompanyListRow({
  company,
  themeClasses,
  isDarkMode,
  onDetails,
  onEdit,
  onDelete,
  onActivate,
  onSoftDelete,
  onSources,
  onRetransmissions,
  isSelectionMode = false,
  isSelected = false,
  onToggleSelection,
}: CompanyListRowProps) {
  const getStatusColor = (status_id: string): string => {
    switch (status_id) {
      case "jXmjzHcBD5E1J0wjwHzq": // facturable
        return "facturable";
      case "Y2aD7bE1fF6jM3kO9hP5": // suspended
        return "text-red-500 bg-red-500/10";
      default:
        return "text-gray-500 bg-gray-500/10";
    }
  };

  const getStatusLabel = (status_id: string) => {
    switch (status_id) {
      case "jXmjzHcBD5E1J0wjwHzq": // facturable
        return "Activa";
      case "Y2aD7bE1fF6jM3kO9hP5": // suspended
        return "Suspendida";
      case "D4h7DGEqSEXZcG0ByStX": // deleted
        return "Eliminada";
      default:
        return "Desconocida";
    }
  };

  const handleRowClick = () => {
    if (isSelectionMode && onToggleSelection) {
      onToggleSelection(company);
    }
  };

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 hover:shadow-md transition-all duration-300 flex items-center justify-between ${
        isSelectionMode ? "cursor-pointer" : ""
      } ${isSelected ? "ring-2 ring-primary-500" : ""}`}
      onClick={isSelectionMode ? handleRowClick : undefined}
    >
      {/* Selection Checkbox */}
      {isSelectionMode && (
        <div className="flex items-center mr-4">
          <div
            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
              isSelected
                ? "bg-primary-500 border-primary-500"
                : isDarkMode
                ? "border-gray-500"
                : "border-gray-300"
            }`}
          >
            {isSelected && <i className="ri-check-line text-white text-sm"></i>}
          </div>
        </div>
      )}

      {/* Left - Company Info */}
      <div className="flex items-center space-x-4 flex-1 min-w-0">
        {company.logo ? (
          <img
            src={company.logo}
            alt={company.name}
            className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
          />
        ) : (
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
              isDarkMode ? "bg-[#4A4A4A]" : "bg-gray-100"
            }`}
          >
            <i
              className={`ri-building-2-line text-lg ${themeClasses.textSecondary}`}
            ></i>
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className={`font-semibold ${themeClasses.text} truncate`}>
              {company.name}
            </h3>
            <span
              className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap ${getStatusColor(
                company.status_id || "",
              )}`}
            >
              {getStatusLabel(company.status_id || "")}
            </span>
          </div>
          <p className={`text-sm ${themeClasses.textSecondary} truncate`}>
            {company.business_name}
          </p>
        </div>
      </div>

      {/* Center - Contact Info */}
      <div className="hidden md:flex items-center space-x-8 flex-1">
        <div className="flex items-center space-x-2">
          <i
            className={`ri-mail-line text-sm ${themeClasses.textSecondary}`}
          ></i>
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {company.email}
          </span>
        </div>
        {company.phone && (
          <div className="flex items-center space-x-2">
            <i
              className={`ri-phone-line text-sm ${themeClasses.textSecondary}`}
            ></i>
            <span className={`text-sm ${themeClasses.textSecondary}`}>
              {company.phone}
            </span>
          </div>
        )}
      </div>

      {/* Right - Actions */}
      <div className="shrink-0">
        <CompanyActionsMenu
          company={company}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          onDetails={onDetails}
          onEdit={onEdit}
          onDelete={onDelete}
          onActivate={onActivate}
          onSoftDelete={onSoftDelete}
          onSources={onSources}
          onRetransmissions={onRetransmissions}
        />
      </div>
    </div>
  );
}
